/**
 * API credentials — unified panel (Feb 27 2026).
 *
 * Single inline section that surfaces BOTH credential systems behind
 * a single "API credentials" label:
 *
 *   1. Acquirer (PayTech) keys
 *      POST /admin/merchants/{id}/api-credentials
 *      Fields: paytech_api_key, paytech_secret, paytech_account_id
 *
 *   2. Gateway (Shop) keys
 *      POST /admin/merchants/{id}/shop-credentials/issue
 *      Fields: api_key, signing_key, env, methods, currencies,
 *              webhook_url, return_url, base_url, api_docs_url, CC
 *
 * Greenfield merchants see ONE "Issue credentials" button that opens a
 * merged modal fanning out both POSTs sequentially. Established
 * merchants get per-system Re-reveal + Rotate buttons that drive each
 * existing modal independently — preserves the distinct lifecycles
 * (acquirer rotations vs shop key rotations).
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Copy, ExternalLink, Eye,
  Loader2, Lock, Mail, PlusCircle, RefreshCw, ShieldAlert, X, KeyRound, Check, Globe,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { PIIRevealConfirm } from "../../../components/admin/PIIRevealConfirm";
import { Section, Meta, fmtDate } from "./primitives";

const METHODS = ["card", "payid", "apple_pay", "google_pay", "sepa", "bank_transfer"];
const CURRENCIES = ["EUR", "GBP", "USD", "CHF", "AED", "SGD", "AUD", "CAD", "JPY"];
const REVEAL_REASON_MIN = 20;

// ---------- Unified panel ----------

export function APICredentialsPanel({ merchantId, status }) {
  const [paytechMeta, setPaytechMeta] = useState(null);
  const [shopMeta, setShopMeta] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal flags
  const [mergedIssueOpen, setMergedIssueOpen] = useState(false);
  const [paytechRotateOpen, setPaytechRotateOpen] = useState(false);
  const [shopRotateOpen, setShopRotateOpen] = useState(false);
  const [addPropertyOpen, setAddPropertyOpen] = useState(false);

  // PayTech reveal
  const [paytechRevealOpen, setPaytechRevealOpen] = useState(false);
  const [paytechRevealedCreds, setPaytechRevealedCreds] = useState(null);

  // Shop reveal
  const [shopRevealOpen, setShopRevealOpen] = useState(false);
  const [shopRevealedCreds, setShopRevealedCreds] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      // Brief 3.14 (Feb 27 2026) — also fetch the multi-property list.
      // The Primary entry is a virtual mirror of the top-level
      // `shop_credentials` (we still hit /shop-credentials for the
      // un-redacted base/api_docs URLs that drive the rotation modal's
      // prefill), and additional_properties are surfaced as extra
      // cards below the Primary card.
      const [paytechRes, shopRes, propsRes] = await Promise.all([
        api.get(`/admin/merchants/${merchantId}/api-credentials`).catch(() => ({ data: null })),
        api.get(`/admin/merchants/${merchantId}/shop-credentials`).catch(() => ({ data: null })),
        api.get(`/admin/merchants/${merchantId}/properties`).catch(() => ({ data: { properties: [] } })),
      ]);
      setPaytechMeta(paytechRes.data);
      setShopMeta(shopRes.data);
      setProperties(propsRes.data?.properties || []);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [merchantId]);

  const paytechCurrent = paytechMeta?.current;
  const shopIssued = shopMeta?.issued_at;
  const nothingIssued = !paytechCurrent && !shopIssued;

  let subtitle;
  if (loading) subtitle = "Loading…";
  else if (nothingIssued) subtitle = "Not issued";
  else if (paytechCurrent && shopIssued) subtitle = `Acquirer + gateway issued`;
  else if (paytechCurrent) subtitle = `Acquirer issued · gateway pending`;
  else subtitle = `Gateway issued · acquirer pending`;

  return (
    <Section icon={Lock} title="API credentials" testId="merchant-section-credentials"
             subtitle={subtitle}>
      {loading && (
        <div className="text-zinc-500 text-[12.5px] flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading credentials state…
        </div>
      )}

      {!loading && nothingIssued && (
        <div className="rounded-xl border border-dashed border-white/[0.08] bg-white/[0.01] p-5"
             data-testid="merchant-credentials-empty">
          <div className="text-[13px] text-white">No credentials issued yet.</div>
          <div className="text-[12px] text-zinc-500 mt-1 leading-relaxed">
            Issue both the <b>acquirer (PayTech) keys</b> (Neptune→PayTech) and the
            <b> gateway (shop) keys</b> (merchant→Neptune) in one step. The acquirer
            keys are minted in PayTech&apos;s back office; the shop keys are minted
            in the gateway back office. Both are encrypted at rest and audit-logged
            on every reveal.
          </div>
          <button
            type="button"
            onClick={() => setMergedIssueOpen(true)}
            data-testid="shop-creds-issue-cta"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2 text-[12.5px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors"
          >
            <KeyRound className="h-3.5 w-3.5" /> Issue credentials
          </button>
        </div>
      )}

      {!loading && !nothingIssued && (
        <div className="space-y-4">
          {/* Acquirer (PayTech) sub-block */}
          <PaytechSubBlock
            merchantId={merchantId}
            status={status}
            paytechMeta={paytechMeta}
            onRefresh={refresh}
            onRotate={() => setPaytechRotateOpen(true)}
            onReveal={() => setPaytechRevealOpen(true)}
            onIssueInitial={() => setMergedIssueOpen(true)}
          />

          {/* Gateway (Shop) sub-block — Primary property */}
          <ShopSubBlock
            merchantId={merchantId}
            shopMeta={shopMeta}
            primaryUrl={properties.find((p) => p.is_primary)?.url}
            onRefresh={refresh}
            onRotate={() => setShopRotateOpen(true)}
            onReveal={() => setShopRevealOpen(true)}
            onIssueInitial={() => setMergedIssueOpen(true)}
          />

          {/* Brief 3.14 (Feb 27 2026) — additional properties.
             Each card maps to one entry in
             `merchant_applications.additional_properties` and uses the
             dedicated /properties/{pid}/shop-credentials/* endpoints,
             so it never touches the Primary's top-level shop_credentials. */}
          {properties.filter((p) => !p.is_primary).map((p) => (
            <AdditionalPropertyCard
              key={p.property_id}
              merchantId={merchantId}
              property={p}
              onChanged={refresh}
            />
          ))}

          {/* Add property CTA — visible once at least one credentials
             system is wired up. Companies with multiple URLs (e.g.
             ThinkTank's shop1 + shop2) add the extra URLs here. */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setAddPropertyOpen(true)}
              data-testid="merchant-add-property-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-3.5 py-1.5 text-[12px] text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-200 transition-colors"
            >
              <PlusCircle className="h-3.5 w-3.5" /> Add property (URL)
            </button>
          </div>
        </div>
      )}

      {addPropertyOpen && (
        <AddPropertyModal
          merchantId={merchantId}
          onClose={() => setAddPropertyOpen(false)}
          onAdded={() => { setAddPropertyOpen(false); refresh(); }}
        />
      )}

      {/* Merged first-time issuance modal (handles BOTH systems). */}
      {mergedIssueOpen && (
        <MergedIssueCredentialsModal
          merchantId={merchantId}
          existingPaytech={!!paytechCurrent}
          existingShop={!!shopIssued}
          onClose={() => setMergedIssueOpen(false)}
          onIssued={() => { setMergedIssueOpen(false); refresh(); }}
        />
      )}

      {/* PayTech rotation modal */}
      {paytechRotateOpen && (
        <IssuePaytechModal
          merchantId={merchantId}
          isRotation={!!paytechCurrent}
          onClose={() => setPaytechRotateOpen(false)}
          onIssued={() => { setPaytechRotateOpen(false); refresh(); }}
        />
      )}

      {/* Shop rotation modal */}
      {shopRotateOpen && (
        <IssueShopModal
          merchantId={merchantId}
          existingShop={shopMeta}
          onClose={() => setShopRotateOpen(false)}
          onIssued={() => { setShopRotateOpen(false); refresh(); }}
        />
      )}

      {/* PayTech reveal */}
      <PIIRevealConfirm
        open={paytechRevealOpen}
        onClose={() => setPaytechRevealOpen(false)}
        onConfirmed={(data) => {
          setPaytechRevealedCreds(data?.credentials || null);
          setPaytechRevealOpen(false);
          refresh();
        }}
        apiPath={`/admin/merchants/${merchantId}/api-credentials/reveal`}
        entityLabel="Acquirer (PayTech) API credentials"
        entityDescription="The 3 PayTech fields will be decrypted and shown once. Counter increments. The reason you provide is logged."
        testId="credentials-reveal-confirm"
      />
      {paytechRevealedCreds && (
        <CredentialsRevealedModal
          title="PayTech credentials"
          credentials={paytechRevealedCreds}
          onClose={() => setPaytechRevealedCreds(null)}
        />
      )}

      {/* Shop reveal */}
      <PIIRevealConfirm
        open={shopRevealOpen}
        onClose={() => setShopRevealOpen(false)}
        onConfirmed={(data) => {
          setShopRevealedCreds(data || null);
          setShopRevealOpen(false);
          refresh();
        }}
        apiPath={`/admin/merchants/${merchantId}/shop-credentials/reveal`}
        entityLabel="Gateway (shop) keys"
        entityDescription="The Shop API Key + Signing Key will be decrypted and shown once. The reason you provide is audit-logged."
        testId="shop-credentials-reveal-confirm"
      />
      {shopRevealedCreds && (
        <CredentialsRevealedModal
          title="Gateway shop keys"
          credentials={{
            api_key: shopRevealedCreds.api_key,
            signing_key: shopRevealedCreds.signing_key,
          }}
          onClose={() => setShopRevealedCreds(null)}
        />
      )}
    </Section>
  );
}

// ---------- Sub-blocks ----------

function PaytechSubBlock({ merchantId, status, paytechMeta, onRotate, onReveal, onIssueInitial }) {
  const current = paytechMeta?.current;
  const history = paytechMeta?.history || [];
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-5"
         data-testid="merchant-paytech-subblock">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
            Acquirer (PayTech) keys
          </div>
          <div className="text-[12px] text-zinc-400 mt-0.5">
            Neptune→PayTech. Used by the platform to route transactions upstream.
          </div>
        </div>
        {current
          ? <span className="text-[11px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Issued</span>
          : <span className="text-[11px] text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Not issued</span>}
      </div>

      {!current && (
        <button type="button" onClick={onIssueInitial}
                data-testid="merchant-paytech-issue-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-3.5 py-1.5 text-[12px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors">
          <Lock className="h-3 w-3" /> Issue acquirer keys
        </button>
      )}

      {current && (
        <>
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <Meta label="Key ID" value={current.key_id} />
            <Meta label="Issued by" value={current.created_by_email || "—"} />
            <Meta label="Issued at" value={fmtDate(current.created_at)} />
            <Meta label="Fields" value={(current.field_names || []).join(", ")} />
            <Meta
              label="Revealed to merchant"
              value={current.revealed_to_merchant_at
                ? fmtDate(current.revealed_to_merchant_at)
                : "Not yet"}
            />
            <Meta label="Admin re-reveals" value={String(current.revealed_to_admin_count || 0)} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={onReveal}
                    data-testid="merchant-credentials-reveal-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-4 py-2 text-[12.5px] text-amber-200 hover:bg-amber-400/[0.12] transition-colors">
              <Eye className="h-3.5 w-3.5" /> Re-reveal
            </button>
            <button type="button" onClick={onRotate}
                    data-testid="merchant-credentials-rotate-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Rotate (issue new)
            </button>
            {(status === "approved" || status === "live") && (
              <a
                href={process.env.REACT_APP_BACKOFFICE_URL || "https://backoffice.neptunepay.io"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  api.post(`/admin/merchants/${merchantId}/backoffice-visit`).catch(() => {});
                }}
                data-testid="merchant-backoffice-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.04] px-4 py-2 text-[12.5px] text-cyan-200 hover:bg-cyan-400/[0.10] transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open gateway back office
              </a>
            )}
          </div>
          {history.length > 0 && (
            <details className="mt-4 text-[12px] text-zinc-500" data-testid="merchant-credentials-history">
              <summary className="cursor-pointer text-zinc-400 hover:text-white">Superseded ({history.length})</summary>
              <ul className="mt-2 space-y-1">
                {history.map((h) => (
                  <li key={h.key_id} className="text-zinc-500">
                    {h.key_id.slice(0, 8)}… · superseded {fmtDate(h.superseded_at)}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function ShopSubBlock({ merchantId, shopMeta, primaryUrl, onRotate, onReveal, onIssueInitial }) {
  const issued = shopMeta?.issued_at;
  const [resending, setResending] = useState(false);
  // Brief 3.13.4 (Feb 27 2026) — dropped window.confirm() here. Some
  // browsers / corporate setups suppress it silently, which made the
  // click look like "nothing happens". Resending a handover email is
  // a benign idempotent action — no need to gate it behind a confirm.
  // Immediate toast.loading() gives the AM visible feedback the moment
  // the request fires.
  const resendHandover = async () => {
    if (resending) return;
    setResending(true);
    const toastId = toast.loading("Sending handover email…");
    try {
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/credentials-issued/resend`, {});
      const to = data?.email?.to || "the merchant";
      const id = data?.email?.resend_id || "";
      toast.success(
        `Handover email queued to ${to}${id ? ` · id ${id.slice(0,8)}…` : ""}`,
        { id: toastId, duration: 6000 },
      );
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail
        || err?.message
        || "Unknown error";
      // 502 = Resend rejected (key invalid / send failed)
      // 422 = backend rejected (no contact_email / no shop creds)
      // 401/403 = session expired
      // else = network / CORS
      toast.error(
        `Resend failed${status ? ` (${status})` : ""}: ${detail}`,
        { id: toastId, duration: 10000 },
      );
    } finally {
      setResending(false);
    }
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-5"
         data-testid="merchant-shop-subblock">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
            Gateway (shop) keys · Primary
          </div>
          <div className="text-[12px] text-zinc-400 mt-0.5">
            Merchant→Neptune. What the merchant pastes into their checkout integration.
          </div>
          {primaryUrl && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-zinc-300"
                 data-testid="merchant-shop-primary-url">
              <Globe className="h-3 w-3 text-zinc-500" />
              <span className="font-mono">{primaryUrl}</span>
            </div>
          )}
        </div>
        {issued
          ? <span className="text-[11px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Issued</span>
          : <span className="text-[11px] text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Not issued</span>}
      </div>

      {!issued && (
        <button type="button" onClick={onIssueInitial}
                data-testid="merchant-shop-issue-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-3.5 py-1.5 text-[12px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors">
          <KeyRound className="h-3 w-3" /> Issue gateway keys
        </button>
      )}

      {issued && (
        <>
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <Meta label="Environment" value={shopMeta.environment || "—"} />
            <Meta label="Issued by" value={shopMeta.issued_by || "—"} />
            <Meta label="Issued at" value={fmtDate(shopMeta.issued_at)} />
            <Meta label="Admin re-reveals" value={String(shopMeta.revealed_to_admin_count || 0)} />
            <Meta label="API key" value={shopMeta.api_key_preview || "—"} />
            <Meta label="Signing key" value={shopMeta.signing_key_preview || "—"} />
            <Meta label="Methods" value={(shopMeta.enabled_methods || []).join(", ") || "—"} />
            <Meta label="Currencies" value={(shopMeta.enabled_currencies || []).join(", ") || "—"} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={onReveal}
                    data-testid="merchant-shop-reveal-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-4 py-2 text-[12.5px] text-amber-200 hover:bg-amber-400/[0.12] transition-colors">
              <Eye className="h-3.5 w-3.5" /> Re-reveal
            </button>
            <button type="button" onClick={onRotate}
                    data-testid="merchant-shop-rotate-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Re-issue (rotate)
            </button>
            <button type="button" onClick={resendHandover} disabled={resending}
                    data-testid="merchant-shop-resend-handover-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2 text-[12.5px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors disabled:opacity-40">
              {resending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <Mail className="h-3.5 w-3.5" /> Resend handover email
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Merged first-time issuance modal ----------

function MergedIssueCredentialsModal({ merchantId, existingPaytech, existingShop, onClose, onIssued }) {
  // PayTech state
  const [paytechForm, setPaytechForm] = useState({
    paytech_api_key: "", paytech_secret: "", paytech_account_id: "",
  });
  // Shop state
  const [env, setEnv] = useState("sandbox");
  const [methods, setMethods] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [signingKey, setSigningKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://engine.neptunepay.io");
  const [apiDocsUrl, setApiDocsUrl] = useState("https://neptunepay.api-docs.info");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [returnUrl, setReturnUrl] = useState("");
  const [additionalRecipients, setAdditionalRecipients] = useState("");

  // Which subsystems to issue this round
  const [includePaytech, setIncludePaytech] = useState(!existingPaytech);
  const [includeShop, setIncludeShop] = useState(!existingShop);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Captured one-time secrets after a successful POST
  const [issuedShop, setIssuedShop] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const toggleIn = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const paytechValid = paytechForm.paytech_api_key.trim().length >= 8
    && paytechForm.paytech_secret.trim().length >= 8
    && paytechForm.paytech_account_id.trim().length >= 4;

  const shopValid = apiKey.trim().length >= 8
    && signingKey.trim().length >= 8
    && baseUrl.trim().length > 0
    && apiDocsUrl.trim().length > 0;

  const canSubmit = (includePaytech ? paytechValid : true)
    && (includeShop ? shopValid : true)
    && (includePaytech || includeShop);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    const errors = [];

    // 1) PayTech first
    if (includePaytech) {
      try {
        await api.post(`/admin/merchants/${merchantId}/api-credentials`, {
          paytech_api_key: paytechForm.paytech_api_key.trim(),
          paytech_secret: paytechForm.paytech_secret.trim(),
          paytech_account_id: paytechForm.paytech_account_id.trim(),
        });
      } catch (err) {
        errors.push(`Acquirer (PayTech): ${err?.response?.data?.detail || "issue failed"}`);
      }
    }

    // 2) Shop second
    let shopResp = null;
    if (includeShop) {
      try {
        const recipients = additionalRecipients
          .split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
        const { data } = await api.post(
          `/admin/merchants/${merchantId}/shop-credentials/issue`,
          {
            api_key: apiKey.trim(),
            signing_key: signingKey.trim(),
            gateway_base_url: baseUrl.trim(),
            api_docs_url: apiDocsUrl.trim(),
            environment: env,
            enabled_methods: methods,
            enabled_currencies: currencies,
            webhook_url: webhookUrl || null,
            return_url: returnUrl || null,
            additional_recipients: recipients,
          },
        );
        shopResp = data;
      } catch (err) {
        errors.push(`Gateway (shop): ${err?.response?.data?.detail || "issue failed"}`);
      }
    }

    if (errors.length > 0) {
      setError(errors.join(" · "));
      setSubmitting(false);
      // Partial success: still surface the shop one-time-view if it
      // succeeded — the AM must capture it before closing.
      if (shopResp) {
        setIssuedShop(shopResp);
        setApiKey(""); setSigningKey("");
      } else {
        // Total failure path — let the user fix and retry.
        return;
      }
      return;
    }

    // Full success
    if (shopResp) {
      setIssuedShop(shopResp);
      setApiKey(""); setSigningKey("");
      toast.success("Credentials issued");
    } else {
      toast.success("Acquirer credentials issued");
      onIssued();
    }
    setSubmitting(false);
  };

  // One-time view of the shop secrets after issuance
  if (issuedShop) {
    return (
      <SuccessReveal
        issued={issuedShop}
        partialError={error}
        onDone={() => { setIssuedShop(null); onIssued(); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-2xl rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="merged-issue-credentials-modal">
        <header className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Issue credentials</div>
            <div className="text-white text-[15px] mt-1">Acquirer + gateway keys</div>
            <div className="text-zinc-500 text-[12px] mt-1 leading-relaxed">
              Paste both sets of values from the back-office tools that mint them. Encrypted at rest. Format checked locally.
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="merged-issue-close"
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Toggle which sub-system to issue (auto-checked based on state) */}
        <div className="flex items-center gap-4 mb-5 text-[12px]">
          <label className="inline-flex items-center gap-2 text-zinc-300">
            <input type="checkbox" checked={includePaytech}
              onChange={(e) => setIncludePaytech(e.target.checked)}
              data-testid="merged-include-paytech"
              className="rounded border-white/[0.10] bg-white/[0.05]" />
            Acquirer (PayTech) keys
            {existingPaytech && <span className="text-zinc-500 text-[10.5px] ml-1">(re-rotate)</span>}
          </label>
          <label className="inline-flex items-center gap-2 text-zinc-300">
            <input type="checkbox" checked={includeShop}
              onChange={(e) => setIncludeShop(e.target.checked)}
              data-testid="merged-include-shop"
              className="rounded border-white/[0.10] bg-white/[0.05]" />
            Gateway (shop) keys
            {existingShop && <span className="text-zinc-500 text-[10.5px] ml-1">(re-issue)</span>}
          </label>
        </div>

        {/* PayTech section */}
        {includePaytech && (
          <fieldset className="mb-5 rounded-xl border border-white/[0.08] bg-white/[0.015] p-4">
            <legend className="px-2 text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
              Acquirer (PayTech) — Neptune→PayTech
            </legend>
            <div className="grid grid-cols-1 gap-3 mt-2">
              {[
                { key: "paytech_api_key", label: "API key", placeholder: "pk_live_…" },
                { key: "paytech_secret", label: "Secret", placeholder: "sk_live_…" },
                { key: "paytech_account_id", label: "Account ID", placeholder: "acct_…" },
              ].map((f) => (
                <label key={f.key} className="block">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{f.label}</span>
                  <input
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    value={paytechForm[f.key]}
                    onChange={(e) => setPaytechForm({ ...paytechForm, [f.key]: e.target.value })}
                    data-testid={`merged-paytech-${f.key}`}
                    placeholder={f.placeholder}
                    className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono"
                  />
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {/* Shop section */}
        {includeShop && (
          <fieldset className="mb-5 rounded-xl border border-white/[0.08] bg-white/[0.015] p-4">
            <legend className="px-2 text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
              Gateway (shop) — Merchant→Neptune
            </legend>
            <div className="mt-2">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Environment</div>
              <div className="flex gap-2">
                {["sandbox", "production"].map((e) => (
                  <button key={e} type="button" onClick={() => setEnv(e)} data-testid={`merged-env-${e}`}
                    className={`rounded-full px-3.5 py-1.5 text-[12px] border transition-colors ${
                      env === e ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                        : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                    }`}>{e}</button>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Shop API key</span>
                <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  data-testid="merged-shop-api-key"
                  placeholder="np_sk_live_…" autoComplete="off" spellCheck={false}
                  className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Shop signing key</span>
                <input type="text" value={signingKey} onChange={(e) => setSigningKey(e.target.value)}
                  data-testid="merged-shop-signing-key"
                  placeholder="whsec_…" autoComplete="off" spellCheck={false}
                  className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Base URL</span>
                <input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                  data-testid="merged-shop-base-url"
                  className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">API docs URL</span>
                <input type="url" value={apiDocsUrl} onChange={(e) => setApiDocsUrl(e.target.value)}
                  data-testid="merged-shop-api-docs"
                  className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
              </label>
            </div>
            <div className="mt-4">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Payment methods</div>
              <div className="flex flex-wrap gap-2">
                {METHODS.map((m) => {
                  const on = methods.includes(m);
                  return (
                    <button key={m} type="button" onClick={() => toggleIn(methods, setMethods, m)}
                      data-testid={`merged-method-${m}`}
                      className={`rounded-full px-3 py-1.5 text-[11.5px] border transition-colors ${
                        on ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                          : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                      }`}>{m}</button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Currencies</div>
              <div className="flex flex-wrap gap-2">
                {CURRENCIES.map((c) => {
                  const on = currencies.includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleIn(currencies, setCurrencies, c)}
                      data-testid={`merged-ccy-${c}`}
                      className={`rounded-full px-3 py-1.5 text-[11.5px] border transition-colors ${
                        on ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                          : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                      }`}>{c}</button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Webhook URL (merchant-provided)</span>
                <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
                  data-testid="merged-webhook"
                  placeholder="https://shop.example/neptune/webhook"
                  className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Return URL (after checkout)</span>
                <input type="url" value={returnUrl} onChange={(e) => setReturnUrl(e.target.value)}
                  data-testid="merged-return"
                  placeholder="https://shop.example/order/complete"
                  className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">CC handover email (optional)</span>
                <input type="text" value={additionalRecipients}
                  onChange={(e) => setAdditionalRecipients(e.target.value)}
                  data-testid="merged-additional-recipients"
                  placeholder="cto@merchant.com, dev-lead@merchant.com"
                  className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
                <span className="mt-1 block text-[10.5px] text-zinc-600">
                  Comma- or space-separated. CC&apos;d on the handover email (merchant&apos;s contact email is always TO&apos;d).
                </span>
              </label>
            </div>
          </fieldset>
        )}

        {error && (
          <div className="mb-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200" data-testid="merged-issue-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="merged-issue-cancel"
                  className="rounded-full border border-white/[0.08] bg-white/[0.01] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit || submitting}
                  data-testid="merged-issue-confirm"
                  className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2 text-[12.5px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Issue
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- PayTech-only rotation modal (for established merchants) ----------

function IssuePaytechModal({ merchantId, isRotation, onClose, onIssued }) {
  const [form, setForm] = useState({
    paytech_api_key: "", paytech_secret: "", paytech_account_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const valid = form.paytech_api_key.trim().length >= 8
    && form.paytech_secret.trim().length >= 8
    && form.paytech_account_id.trim().length >= 4;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/admin/merchants/${merchantId}/api-credentials`, {
        paytech_api_key: form.paytech_api_key.trim(),
        paytech_secret: form.paytech_secret.trim(),
        paytech_account_id: form.paytech_account_id.trim(),
      });
      setForm({ paytech_api_key: "", paytech_secret: "", paytech_account_id: "" });
      onIssued();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not issue credentials.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-lg rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="issue-credentials-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{isRotation ? "Rotate" : "Issue"} acquirer keys</div>
            <div className="text-white text-[15px] mt-1">PayTech keys</div>
            <div className="text-zinc-500 text-[12px] mt-1">
              Paste the 3 fields from PayTech&apos;s back office. Encrypted at rest. Format checked locally.
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>

        {[
          { key: "paytech_api_key", label: "API key", placeholder: "pk_live_…" },
          { key: "paytech_secret", label: "Secret", placeholder: "sk_live_…" },
          { key: "paytech_account_id", label: "Account ID", placeholder: "acct_…" },
        ].map((f) => (
          <div key={f.key} className="mb-3">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{f.label}</label>
            <input
              type="password" autoComplete="off" spellCheck={false}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              data-testid={`issue-credentials-${f.key}`}
              placeholder={f.placeholder}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/20 font-mono"
            />
          </div>
        ))}

        {error && (
          <div className="mb-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200" data-testid="issue-credentials-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="issue-credentials-cancel"
                  className="rounded-full border border-white/[0.08] bg-white/[0.01] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!valid || submitting}
                  data-testid="issue-credentials-confirm"
                  className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2 text-[12.5px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isRotation ? "Rotate" : "Issue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Shop-only re-issue modal (for established merchants) ----------

function IssueShopModal({ merchantId, existingShop, onClose, onIssued }) {
  const [env, setEnv] = useState(existingShop?.environment || "sandbox");
  const [methods, setMethods] = useState(existingShop?.enabled_methods || []);
  const [currencies, setCurrencies] = useState(existingShop?.enabled_currencies || []);
  const [apiKey, setApiKey] = useState("");
  const [signingKey, setSigningKey] = useState("");
  const [baseUrl, setBaseUrl] = useState(existingShop?.gateway_base_url || "https://engine.neptunepay.io");
  const [apiDocsUrl, setApiDocsUrl] = useState(existingShop?.api_docs_url || "https://neptunepay.api-docs.info");
  const [webhookUrl, setWebhookUrl] = useState(existingShop?.webhook_url || "");
  const [returnUrl, setReturnUrl] = useState(existingShop?.return_url || "");
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const toggleIn = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const valid = apiKey.trim().length >= 8 && signingKey.trim().length >= 8
    && baseUrl.trim() && apiDocsUrl.trim();

  const submit = async () => {
    if (!valid) return;
    if (existingShop && !window.confirm(
      "Re-issuing replaces the existing Shop API Key + Signing Key. Any integration the merchant has built will stop working until you give them the new keys. Continue?"
    )) return;
    setSubmitting(true); setError("");
    try {
      const recipients = additionalRecipients
        .split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/shop-credentials/issue`,
        {
          api_key: apiKey.trim(),
          signing_key: signingKey.trim(),
          gateway_base_url: baseUrl.trim(),
          api_docs_url: apiDocsUrl.trim(),
          environment: env,
          enabled_methods: methods,
          enabled_currencies: currencies,
          webhook_url: webhookUrl || null,
          return_url: returnUrl || null,
          additional_recipients: recipients,
        },
      );
      setIssued(data);
      setApiKey(""); setSigningKey("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not issue credentials.");
      setSubmitting(false);
    }
  };

  if (issued) {
    return (
      <SuccessReveal
        issued={issued}
        partialError=""
        onDone={() => { setIssued(null); onIssued(); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-xl rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="shop-credentials-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{existingShop ? "Re-issue" : "Issue"} gateway keys</div>
            <div className="text-white text-[15px] mt-1">Gateway (shop) keys</div>
            <div className="text-zinc-500 text-[12px] mt-1">
              Mint these in the gateway back office, then paste them here. Encrypted at rest.
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Environment</div>
          <div className="flex gap-2">
            {["sandbox", "production"].map((e) => (
              <button key={e} type="button" onClick={() => setEnv(e)} data-testid={`shop-creds-env-${e}`}
                className={`rounded-full px-3.5 py-1.5 text-[12px] border transition-colors ${
                  env === e ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                    : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                }`}>{e}</button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Shop API key</span>
            <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              data-testid="shop-creds-api-key"
              placeholder="np_sk_live_…" autoComplete="off" spellCheck={false}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Shop signing key</span>
            <input type="text" value={signingKey} onChange={(e) => setSigningKey(e.target.value)}
              data-testid="shop-creds-signing-key"
              placeholder="whsec_…" autoComplete="off" spellCheck={false}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Base URL</span>
            <input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
              data-testid="shop-creds-base-url"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">API docs URL</span>
            <input type="url" value={apiDocsUrl} onChange={(e) => setApiDocsUrl(e.target.value)}
              data-testid="shop-creds-api-docs"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
          </label>
        </div>

        <div className="mt-4">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Payment methods</div>
          <div className="flex flex-wrap gap-2">
            {METHODS.map((m) => {
              const on = methods.includes(m);
              return (
                <button key={m} type="button" onClick={() => toggleIn(methods, setMethods, m)}
                  data-testid={`shop-creds-method-${m}`}
                  className={`rounded-full px-3 py-1.5 text-[11.5px] border transition-colors ${
                    on ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                      : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                  }`}>{m}</button>
              );
            })}
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Currencies</div>
          <div className="flex flex-wrap gap-2">
            {CURRENCIES.map((c) => {
              const on = currencies.includes(c);
              return (
                <button key={c} type="button" onClick={() => toggleIn(currencies, setCurrencies, c)}
                  data-testid={`shop-creds-ccy-${c}`}
                  className={`rounded-full px-3 py-1.5 text-[11.5px] border transition-colors ${
                    on ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                      : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                  }`}>{c}</button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Webhook URL</span>
            <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
              data-testid="shop-creds-webhook"
              placeholder="https://shop.example/neptune/webhook"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Return URL</span>
            <input type="url" value={returnUrl} onChange={(e) => setReturnUrl(e.target.value)}
              data-testid="shop-creds-return"
              placeholder="https://shop.example/order/complete"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">CC handover email (optional)</span>
            <input type="text" value={additionalRecipients}
              onChange={(e) => setAdditionalRecipients(e.target.value)}
              data-testid="shop-creds-additional-recipients"
              placeholder="cto@merchant.com"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
          </label>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200" data-testid="shop-creds-error">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="button" onClick={submit} disabled={!valid || submitting}
            data-testid="shop-creds-issue"
            className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/85 hover:bg-cyan-500 text-black px-4 py-1.5 text-[12.5px] font-medium disabled:opacity-50">
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
            {existingShop ? "Re-issue + notify merchant" : "Issue + notify merchant"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Shared one-time success view ----------

function SuccessReveal({ issued, partialError, onDone }) {
  const [copied, setCopied] = useState({});
  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied((c) => ({ ...c, [label]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [label]: false })), 1500);
    } catch { /* ignore */ }
  };
  // Brief 3.13.2 (Feb 27 2026) — surface whether the handover email
  // actually went out so the AM doesn't assume "submit clicked = merchant
  // notified". The backend `email` block has shape:
  //   { sent: true,  to, cc, resend_id }
  //   { sent: false, reason: "no_contact_email" | "exception" | "milestone_skipped_or_already_fired" }
  const email = issued?.email || {};
  const emailSent = email.sent === true;
  const reason = email.reason;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl rounded-2xl border border-amber-400/30 bg-[#0a0d12] p-6 max-h-[90vh] overflow-y-auto"
           role="dialog" data-testid="shop-credentials-success">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-amber-300" />
            <h3 className="text-[15px] font-semibold text-white">Credentials issued (one-time view)</h3>
          </div>
        </header>
        {partialError && (
          <div className="mt-3 rounded-md border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200">
            Partial success — {partialError}
          </div>
        )}

        {/* Email outcome banner */}
        {emailSent && (
          <div className="mt-3 rounded-md border border-emerald-400/30 bg-emerald-400/[0.06] px-3 py-2 text-[12px] text-emerald-200"
               data-testid="handover-email-sent">
            ✓ Handover email queued to <b>{email.to}</b>
            {email.cc && email.cc.length > 0 && <> · CC: {email.cc.join(", ")}</>}
            {email.resend_id && <> · Resend id <code className="font-mono text-[11px]">{email.resend_id.slice(0,8)}…</code></>}
          </div>
        )}
        {!emailSent && reason === "no_contact_email" && (
          <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-400/[0.08] px-3 py-2 text-[12px] text-amber-100"
               data-testid="handover-email-skipped">
            ⚠ <b>Handover email NOT sent</b> — this merchant&apos;s application has no
            <code className="mx-1 font-mono">contact_email</code> on file.
            Copy the keys below now, then close, add the merchant&apos;s contact
            email on the merchant detail page, and click <b>Resend handover email</b>.
            An ops alert has been written to the admin feed.
          </div>
        )}
        {!emailSent && reason === "exception" && (
          <div className="mt-3 rounded-md border border-rose-400/40 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200"
               data-testid="handover-email-failed">
            ✗ <b>Handover email failed to send</b> — Resend rejected the message
            or the API key is missing. The credentials are still saved; check
            the admin ops feed and Resend dashboard for the specific error.
          </div>
        )}
        {!emailSent && reason === "milestone_skipped_or_already_fired" && (
          <div className="mt-3 rounded-md border border-zinc-400/30 bg-zinc-400/[0.04] px-3 py-2 text-[12px] text-zinc-300"
               data-testid="handover-email-skipped-milestone">
            ℹ Handover email was not sent — the <code className="mx-1 font-mono">credentials_issued</code> milestone
            had already fired previously for this merchant (one-time event).
            Use <b>Resend handover email</b> on the merchant detail page if a fresh email is needed.
          </div>
        )}

        <p className="mt-3 text-[13px] text-amber-200 leading-relaxed">
          Copy these now — secret values won&apos;t appear again unless you explicitly reveal them (audit-logged).
        </p>
        <div className="mt-4 space-y-3">
          {[
            ["Shop API Key", issued.api_key, "api_key"],
            ["Shop Signing Key", issued.signing_key, "signing_key"],
            ["Base URL", issued.gateway_base_url, "base_url"],
            ["API docs", issued.api_docs_url, "api_docs"],
          ].map(([label, val, key]) => (
            <div key={key} className="flex items-center gap-2">
              <div className="flex-1 rounded-md border border-white/[0.08] bg-black/40 px-3 py-2">
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-zinc-500">{label}</div>
                <div className="mt-0.5 font-mono text-[12px] text-white break-all">{val}</div>
              </div>
              <button type="button" onClick={() => copy(key, val)}
                data-testid={`shop-creds-copy-${key}`}
                className="rounded-md border border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.08] text-white px-2.5 py-2 text-[11px]">
                {copied[key] ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onDone} data-testid="shop-creds-done"
            className="rounded-full bg-white text-black hover:bg-zinc-200 px-4 py-1.5 text-[12.5px] font-medium">Done</button>
        </div>
      </div>
    </div>
  );
}

function CredentialsRevealedModal({ title, credentials, onClose }) {
  const [copied, setCopied] = useState(null);
  const onCopy = async (key) => {
    try {
      await navigator.clipboard.writeText(credentials[key]);
      setCopied(key);
      setTimeout(() => setCopied(null), 1200);
    } catch { /* noop */ }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-amber-400/30 bg-[#0a0d12] p-6"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="credentials-revealed-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-amber-300">
              <ShieldAlert className="h-4 w-4" />
              <div className="text-[10.5px] uppercase tracking-[0.18em]">Revealed</div>
            </div>
            <div className="text-white text-[15px] mt-1">{title}</div>
            <div className="text-zinc-500 text-[12px] mt-1">Close this dialog to clear the values from memory.</div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="credentials-revealed-close"
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        {Object.entries(credentials).map(([k, v]) => (
          v == null ? null : (
            <div key={k} className="mb-3">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{k}</div>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12px] text-amber-100 font-mono break-all" data-testid={`credentials-revealed-${k}`}>{v}</code>
                <button type="button" onClick={() => onCopy(k)}
                        data-testid={`credentials-revealed-copy-${k}`}
                        className="rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-[11px] text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-200 transition-colors">
                  {copied === k ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Brief 3.14 (Feb 27 2026) — multi-property (URL) support.
// Components below render the per-property cards (non-primary) and the
// "Add property" modal. Primary stays in ShopSubBlock above.
// ============================================================================

function AdditionalPropertyCard({ merchantId, property, onChanged }) {
  const issued = property?.shop_credentials?.issued_at;
  const [editingLabel, setEditingLabel] = useState(false);
  const [label, setLabel] = useState(property.label || "");
  const [url, setUrl] = useState(property.url || "");
  const [busy, setBusy] = useState(false);

  // Inline modal flags
  const [issueOpen, setIssueOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealedCreds, setRevealedCreds] = useState(null);

  const pid = property.property_id;

  const saveEdit = async () => {
    setBusy(true);
    const toastId = toast.loading("Saving…");
    try {
      await api.patch(`/admin/merchants/${merchantId}/properties/${pid}`,
        { label: label.trim(), url: url.trim() });
      toast.success("Property updated", { id: toastId, duration: 3000 });
      setEditingLabel(false);
      onChanged();
    } catch (err) {
      toast.error(
        `Save failed: ${err?.response?.data?.detail || err.message}`,
        { id: toastId, duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archive property "${property.label}"? This hides it from the list but keeps history.`)) return;
    setBusy(true);
    const toastId = toast.loading("Archiving…");
    try {
      await api.post(`/admin/merchants/${merchantId}/properties/${pid}/archive`);
      toast.success("Property archived", { id: toastId, duration: 3000 });
      onChanged();
    } catch (err) {
      toast.error(
        `Archive failed: ${err?.response?.data?.detail || err.message}`,
        { id: toastId, duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const resendHandover = async () => {
    setBusy(true);
    const toastId = toast.loading("Sending handover email…");
    try {
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/properties/${pid}/credentials-issued/resend`);
      const to = data?.email?.to || "the merchant";
      const id = data?.email?.resend_id || "";
      toast.success(
        `Handover email queued to ${to}${id ? ` · id ${id.slice(0,8)}…` : ""}`,
        { id: toastId, duration: 6000 });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message || "Unknown";
      toast.error(`Resend failed${status ? ` (${status})` : ""}: ${detail}`,
        { id: toastId, duration: 10000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-5"
         data-testid={`merchant-property-card-${pid}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
            Property · {property.label}
          </div>
          <div className="text-[12px] text-zinc-400 mt-0.5">
            Gateway (shop) keys for this URL only — independent of the Primary property.
          </div>
          {!editingLabel && (
            <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] text-zinc-300">
              <Globe className="h-3 w-3 text-zinc-500" />
              <span className="font-mono">{property.url}</span>
              <button type="button"
                onClick={() => setEditingLabel(true)}
                data-testid={`property-${pid}-edit-btn`}
                className="ml-2 text-[10.5px] text-zinc-500 hover:text-cyan-300 underline underline-offset-2">
                edit
              </button>
            </div>
          )}
          {editingLabel && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input type="text" value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label"
                data-testid={`property-${pid}-label-input`}
                className="rounded-md border border-white/[0.10] bg-white/[0.02] px-2 py-1 text-[12px] text-white placeholder:text-zinc-600 font-mono w-32 focus:outline-none focus:border-cyan-400/40" />
              <input type="url" value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                data-testid={`property-${pid}-url-input`}
                className="rounded-md border border-white/[0.10] bg-white/[0.02] px-2 py-1 text-[12px] text-white placeholder:text-zinc-600 font-mono flex-1 min-w-64 focus:outline-none focus:border-cyan-400/40" />
              <button type="button" onClick={saveEdit} disabled={busy}
                data-testid={`property-${pid}-save-btn`}
                className="rounded-md bg-cyan-500/85 hover:bg-cyan-500 text-black px-3 py-1 text-[11px] font-medium disabled:opacity-50">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
              </button>
              <button type="button"
                onClick={() => { setEditingLabel(false); setLabel(property.label); setUrl(property.url); }}
                className="text-[11px] text-zinc-400 hover:text-white">
                Cancel
              </button>
            </div>
          )}
        </div>
        {issued
          ? <span className="text-[11px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Issued</span>
          : <span className="text-[11px] text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Not issued</span>}
      </div>

      {issued && (
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <Meta label="Environment" value={property.shop_credentials.environment || "—"} />
          <Meta label="Issued by" value={property.shop_credentials.issued_by || "—"} />
          <Meta label="Issued at" value={fmtDate(property.shop_credentials.issued_at)} />
          <Meta label="API key" value={property.shop_credentials.api_key_preview || "—"} />
          <Meta label="Methods" value={(property.shop_credentials.enabled_methods || []).join(", ") || "—"} />
          <Meta label="Currencies" value={(property.shop_credentials.enabled_currencies || []).join(", ") || "—"} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!issued && (
          <button type="button" onClick={() => setIssueOpen(true)}
            data-testid={`property-${pid}-issue-btn`}
            className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2 text-[12.5px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors">
            <KeyRound className="h-3.5 w-3.5" /> Issue gateway keys
          </button>
        )}
        {issued && (
          <>
            <button type="button" onClick={() => setRevealOpen(true)}
              data-testid={`property-${pid}-reveal-btn`}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-4 py-2 text-[12.5px] text-amber-200 hover:bg-amber-400/[0.12] transition-colors">
              <Eye className="h-3.5 w-3.5" /> Re-reveal
            </button>
            <button type="button" onClick={() => setIssueOpen(true)}
              data-testid={`property-${pid}-rotate-btn`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Re-issue (rotate)
            </button>
            <button type="button" onClick={resendHandover} disabled={busy}
              data-testid={`property-${pid}-resend-handover-btn`}
              className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2 text-[12.5px] text-cyan-200 hover:bg-cyan-400/[0.12] transition-colors disabled:opacity-40">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <Mail className="h-3.5 w-3.5" /> Resend handover
            </button>
          </>
        )}
        <button type="button" onClick={archive} disabled={busy}
          data-testid={`property-${pid}-archive-btn`}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-rose-400/20 bg-rose-400/[0.04] px-3 py-1.5 text-[11.5px] text-rose-300 hover:bg-rose-400/[0.10] transition-colors disabled:opacity-40">
          <X className="h-3 w-3" /> Archive
        </button>
      </div>

      {issueOpen && (
        <IssuePropertyShopModal
          merchantId={merchantId}
          property={property}
          isRotation={!!issued}
          onClose={() => setIssueOpen(false)}
          onIssued={() => { setIssueOpen(false); onChanged(); }}
        />
      )}
      <PIIRevealConfirm
        open={revealOpen}
        onClose={() => setRevealOpen(false)}
        onConfirmed={(data) => {
          setRevealedCreds(data || null);
          setRevealOpen(false);
          onChanged();
        }}
        apiPath={`/admin/merchants/${merchantId}/properties/${pid}/shop-credentials/reveal`}
        entityLabel={`Property "${property.label}" gateway keys`}
        entityDescription="Decrypted and shown once. The reason you provide is audit-logged."
        testId={`property-${pid}-reveal-confirm`}
      />
      {revealedCreds && (
        <CredentialsRevealedModal
          title={`Property "${property.label}" gateway keys`}
          credentials={{
            api_key: revealedCreds.api_key,
            signing_key: revealedCreds.signing_key,
          }}
          onClose={() => setRevealedCreds(null)}
        />
      )}
    </div>
  );
}


function IssuePropertyShopModal({ merchantId, property, isRotation, onClose, onIssued }) {
  const [env, setEnv] = useState(property?.shop_credentials?.environment || "sandbox");
  const [apiKey, setApiKey] = useState("");
  const [signingKey, setSigningKey] = useState("");
  const [methods, setMethods] = useState(property?.shop_credentials?.enabled_methods || []);
  const [currencies, setCurrencies] = useState(property?.shop_credentials?.enabled_currencies || []);
  const [webhook, setWebhook] = useState(property?.shop_credentials?.webhook_url || "");
  const [ret, setRet] = useState(property?.shop_credentials?.return_url || "");
  const [cc, setCc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [issued, setIssuedResp] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const toggleIn = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const valid = apiKey.trim().length >= 8 && signingKey.trim().length >= 8;

  const METHODS = ["card", "payid", "apple_pay", "google_pay", "sepa", "bank_transfer"];
  const CURRENCIES = ["EUR", "GBP", "USD", "CHF", "AED", "SGD", "AUD", "CAD", "JPY"];

  const submit = async () => {
    if (!valid) return;
    if (isRotation && !window.confirm(
      `Re-issuing replaces the existing keys for property "${property.label}". The merchant's integration on ${property.url} will stop working until you give them the new keys. Continue?`
    )) return;
    setSubmitting(true); setError("");
    const recipients = cc.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/properties/${property.property_id}/shop-credentials/issue`,
        {
          api_key: apiKey.trim(), signing_key: signingKey.trim(),
          environment: env, enabled_methods: methods,
          enabled_currencies: currencies,
          webhook_url: webhook || null, return_url: ret || null,
          additional_recipients: recipients,
        });
      setIssuedResp(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not issue credentials.");
      setSubmitting(false);
    }
  };

  if (issued) {
    return (
      <SuccessReveal issued={issued} partialError=""
        onDone={() => { setIssuedResp(null); onIssued(); }} />
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-xl rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid={`property-${property.property_id}-issue-modal`}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
              {isRotation ? "Re-issue" : "Issue"} gateway keys
            </div>
            <div className="text-white text-[15px] mt-1">
              Property: {property.label}
            </div>
            <div className="text-zinc-500 text-[12px] mt-1 font-mono">
              {property.url}
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
            className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Environment</div>
          <div className="flex gap-2">
            {["sandbox", "production"].map((e) => (
              <button key={e} type="button" onClick={() => setEnv(e)}
                className={`rounded-full px-3.5 py-1.5 text-[12px] border transition-colors ${
                  env === e ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                    : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                }`}>{e}</button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Shop API key</span>
            <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder="np_sk_live_…" autoComplete="off" spellCheck={false}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Shop signing key</span>
            <input type="text" value={signingKey} onChange={(e) => setSigningKey(e.target.value)}
              placeholder="whsec_…" autoComplete="off" spellCheck={false}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
          </label>
        </div>

        <div className="mt-4">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Payment methods</div>
          <div className="flex flex-wrap gap-2">
            {METHODS.map((m) => {
              const on = methods.includes(m);
              return (
                <button key={m} type="button" onClick={() => toggleIn(methods, setMethods, m)}
                  className={`rounded-full px-3 py-1.5 text-[11.5px] border transition-colors ${
                    on ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                      : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                  }`}>{m}</button>
              );
            })}
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Currencies</div>
          <div className="flex flex-wrap gap-2">
            {CURRENCIES.map((c) => {
              const on = currencies.includes(c);
              return (
                <button key={c} type="button" onClick={() => toggleIn(currencies, setCurrencies, c)}
                  className={`rounded-full px-3 py-1.5 text-[11.5px] border transition-colors ${
                    on ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                      : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
                  }`}>{c}</button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Webhook URL</span>
            <input type="url" value={webhook} onChange={(e) => setWebhook(e.target.value)}
              placeholder={`${property.url}/neptune/webhook`}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Return URL</span>
            <input type="url" value={ret} onChange={(e) => setRet(e.target.value)}
              placeholder={`${property.url}/order/complete`}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">CC handover (optional)</span>
            <input type="text" value={cc} onChange={(e) => setCc(e.target.value)}
              placeholder="cto@merchant.com"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
          </label>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="button" onClick={submit} disabled={!valid || submitting}
            data-testid={`property-${property.property_id}-issue-confirm`}
            className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/85 hover:bg-cyan-500 text-black px-4 py-1.5 text-[12.5px] font-medium disabled:opacity-50">
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
            {isRotation ? "Re-issue + notify" : "Issue + notify"}
          </button>
        </div>
      </div>
    </div>
  );
}


function AddPropertyModal({ merchantId, onClose, onAdded }) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const valid = label.trim().length >= 1 && url.trim().length >= 4;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/admin/merchants/${merchantId}/properties`,
        { label: label.trim(), url: url.trim() });
      toast.success(`Property "${label}" added`, { duration: 4000 });
      onAdded();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not add property.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="add-property-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Add property</div>
            <div className="text-white text-[15px] mt-1">New URL (additional storefront)</div>
            <div className="text-zinc-500 text-[12px] mt-1 leading-relaxed">
              Use this when a company runs multiple sites that each need their own
              shop keys (e.g. shop1.thinktank.io + shop2.thinktank.io). The
              PayTech acquirer keys at the top stay shared.
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
            className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="block mb-3">
          <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Label</span>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="shop2"
            data-testid="add-property-label"
            className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40" />
        </label>
        <label className="block mb-4">
          <span className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">URL</span>
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://shop2.example.com"
            data-testid="add-property-url"
            className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 font-mono" />
        </label>

        {error && (
          <div className="mb-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="button" onClick={submit} disabled={!valid || submitting}
            data-testid="add-property-confirm"
            className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/85 hover:bg-cyan-500 text-black px-4 py-1.5 text-[12.5px] font-medium disabled:opacity-50">
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
            <PlusCircle className="h-3 w-3" /> Add property
          </button>
        </div>
      </div>
    </div>
  );
}

